/**
 *  Each effect is a pure function that takes the area + toolConstants
 *  and returns either `null` or a patch`{ camera?, toast? }`. The reducer 
 *  applies the patch under Immer.
 */
import { MIC_CATEGORIES } from '../types/models/microphone';
import { isPortOverflow, isUcxSpeakerIncompatible } from '../toolConstants/conferenceroomDesigner/treeFrogPorts';

/**
 * @typedef {Object} AndroidAutoCameraPatch
 * @property {Object} camera - The replacement `area.cameras` object.
 * @property {string} toast - User-facing toast message to surface via `_toastMessage`.
 */

/**
 * Computes the Vidi 280 auto-camera patch for an area that just switched to
 * Platform = Android. Returns null when no patch is needed (bar primary mic
 * provides its own integrated camera, or the camera is already Vidi 280).
 *
 * The reducer is responsible for assigning the patch to the area and
 * setting `_toastMessage`. Keeping the policy here makes it independently
 * testable and easy to extend
 *
 * @param {Object} area - The active area (read-only).
 * @param {Object} toolConstants - The tool's `constants` module (read-only).
 * @returns {AndroidAutoCameraPatch | null}
 */
export const computeAndroidAutoCamera = (area, toolConstants) => {
  if (!area) return null;

  // Skip only when the bar has an INTEGRATED camera (VBC 2500a / VBC 2800).
  // Audio bars (ABC 2500a) and ceiling/table mics need a standalone Vidi 280.
  const primaryMicModel = area.microphones?.model;
  const hasIntegratedCamera = primaryMicModel
    && (primaryMicModel === 'bar' || MIC_CATEGORIES.HAS_CAMERA.has(primaryMicModel));
  if (hasIntegratedCamera) return null;

  // Preserve user's Vidi 250 or Vidi 280 selection — don't override.
  const currentCameraModel = area.cameras?.model;
  if (currentCameraModel === 'vidi280' || currentCameraModel === 'vidi250') return null;

  const cameraEquipment = toolConstants?.equipment?.cameras?.vidi280;
  if (!cameraEquipment) return null;

  const plotVisibility = cameraEquipment.dualImager
    ? { wide_only: true, zoom_only: false }
    : undefined;
  const selectedImager = cameraEquipment.dualImager ? cameraEquipment.wideImager : null;

  const camera = {
    ...(area.cameras || {}),
    hasCamera: true,
    model: 'vidi280',
    modelName: cameraEquipment.modelName,
    coverageRadius: cameraEquipment.dualImager ? selectedImager?.coverageRadius : cameraEquipment.coverageRadius,
    coverageAngle: cameraEquipment.dualImager ? selectedImager?.coverageAngle : cameraEquipment.coverageAngle,
    layout: [],
    qty: 0,
    plotData: {},
    plotVisibility,
  };

  return {
    camera,
    toast: 'Vidi 280 camera added for Android UC platform.',
  };
};

/** Toast shown when an Android design falls back because of the UCC port budget. */
export const PORT_OVERFLOW_FALLBACK_TOAST =
  'Port budget exceeded — peripherals will route through a Tesira DSP + UCC-UCX1000-T (USB).';

/** Toast shown when an Android design falls back because Devio UCX can't drive its speakers at all. */
export const SPEAKER_FALLBACK_TOAST =
  'Selected speakers are not supported by Devio UCX — the system will route through a Tesira DSP + UCC-UCX1000-T (USB).';

/**
 * Android Tesira-via-USB fallback. Routes peripherals through Tesira +
 * UCC-UCX1000-T (USB) when the design exceeds the UCC port budget for its
 * speaker model, or uses a primary speaker Devio UCX can't drive at all (see
 * `isUcxSpeakerIncompatible` — every catalog speaker is supported today).
 * Returns null when neither applies or the design isn't on Android.
 */
export const computePortOverflowFallback = (area) => {
  if (!area) return null;
  if (area.platform !== 'Android') return null;
  const speakerIncompatible = isUcxSpeakerIncompatible(area);
  if (!speakerIncompatible && !isPortOverflow(area)) return null;
  return {
    dspFamily: 'Tesira',
    computeHub: 'Devio UCX 1000',
    reason: speakerIncompatible ? 'speaker-incompatible' : 'port-overflow',
    toast: speakerIncompatible ? SPEAKER_FALLBACK_TOAST : PORT_OVERFLOW_FALLBACK_TOAST,
  };
};
